import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useContentLinkClick } from '../../../components/ExternalLink';
import { useBookingWizard, computeTotal, isOptionStepComplete } from './BookingWizardContext';
import { getBundleLabel } from '../../../utils/getBundleLabel';
import { htmlHasText } from '../../../utils/htmlHasText';

// Step 1 — Seleziona un'opzione. Header con foto/titolo/sottotitolo dell'attività,
// elenco delle opzioni di prezzo; per perPerson/perHour compare lo stepper (partecipanti/ore)
// con il calcolo del totale in tempo reale. Se l'attività ha PIÙ opzioni perPerson, si possono
// combinare nella stessa prenotazione (es. "2 × adulto + 2 × bambino" — feature 2026-09):
// ogni opzione ha il suo stepper e il "−" sotto la quantità minima la deseleziona.
// flat/perHour restano a selezione singola ed esclusiva.
// In coda: la descrizione della prenotazione (priceNote, HTML Quill dell'admin) SENZA titolo
// preposto. "Cosa è incluso" (servicesIncluded) NON compare nel wizard (revisione 2026-07-21).
const StepOption = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const handleContentLinkClick = useContentLinkClick();
    const { activityData, booking, updateBooking } = useBookingWizard();

    // Più opzioni perPerson pubblicate = modalità combinabile
    const perPersonOptions = (activityData.price || []).filter(element => (element.type || 'flat') === 'perPerson');
    const isCombinable = perPersonOptions.length > 1;

    const selections = booking.selections || [];
    const getQuantity = (bundleId) => selections.find(selection => selection.bundleId === bundleId)?.quantity || 0;
    const isSelected = (bundleId) => selections.some(selection => selection.bundleId === bundleId);

    // Ogni modifica alla selezione azzera i partecipanti (il loro numero dipende dalle quantità)
    const setSelections = (nextSelections) => {
        updateBooking({
            selections: nextSelections,
            participants: [{ name: '', surname: '', birthdate: '' }]
        });
    };

    // Un'opzione selezionata in precedenza (stato ripristinato da sessionStorage) e nel frattempo
    // marcata sold out dall'admin decade: resterebbe selezionata senza poter essere deselezionata.
    const soldOutIds = (activityData.price || []).filter(element => element.isSoldOut).map(element => element._id);
    const hasSoldOutSelection = selections.some(selection => soldOutIds.includes(selection.bundleId));
    useEffect(() => {
        if (hasSoldOutSelection) {
            setSelections(selections.filter(selection => !soldOutIds.includes(selection.bundleId)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSoldOutSelection]);

    const selectBundle = (bundle) => {
        if (bundle.isSoldOut) return;
        const type = bundle.type || 'flat';
        const minQty = Number(bundle.minQty) >= 1 ? Number(bundle.minQty) : 1;
        const newSelection = { bundleId: bundle._id, quantity: type === 'flat' ? null : minQty };
        if (isCombinable && type === 'perPerson') {
            if (isSelected(bundle._id)) return;
            // Le opzioni perPerson si sommano tra loro; una selezione flat/perHour precedente decade
            const perPersonIds = new Set(perPersonOptions.map(element => element._id));
            setSelections([...selections.filter(selection => perPersonIds.has(selection.bundleId)), newSelection]);
            return;
        }
        // Selezione esclusiva (flat/perHour, o perPerson quando è l'unica opzione a persona)
        if (selections.length === 1 && isSelected(bundle._id)) return;
        setSelections([newSelection]);
    };

    const stepQuantity = (bundle, delta) => {
        const minQty = Number(bundle.minQty) >= 1 ? Number(bundle.minQty) : 1;
        const maxQty = bundle.maxQty != null ? Number(bundle.maxQty) : null;
        const next = (getQuantity(bundle._id) || minQty) + delta;
        if (next < minQty) {
            // In modalità combinabile, scendere sotto il minimo deseleziona l'opzione
            if (isCombinable && (bundle.type || 'flat') === 'perPerson') {
                setSelections(selections.filter(selection => selection.bundleId !== bundle._id));
            }
            return;
        }
        if (maxQty != null && next > maxQty) return;
        setSelections(selections.map(selection =>
            selection.bundleId === bundle._id ? { ...selection, quantity: next } : selection));
    };

    const total = computeTotal(activityData, booking);
    const canProceed = isOptionStepComplete(activityData, booking);

    return (
        <div className="w-full flex flex-col gap-6 items-center">
            {/* Header attività: foto, titolo, sottotitolo (resta come nella scheda) */}
            <div className="w-full flex flex-col items-center gap-3 text-center">
                {
                    activityData.coverImageUrl?.resizedUrl &&
                    <div className="w-full h-[180px] md:h-[240px] overflow-hidden rounded-xl">
                        <img src={activityData.coverImageUrl.resizedUrl} alt={activityData.title} className="h-full w-full object-cover" />
                    </div>
                }
                <h2>{activityData.title}</h2>
                {activityData.summary && <p className="text-neutral-500">{activityData.summary}</p>}
            </div>

            {/* Con più opzioni perPerson combinabili il titolo passa al plurale */}
            <h3>{t(isCombinable ? 'BookingWizard.option.titleCombinable' : 'BookingWizard.option.title')}</h3>
            {
                isCombinable &&
                <p className="text-sm text-neutral-500 text-center -mt-4">{t('BookingWizard.option.combineHint')}</p>
            }
            <div className="w-full flex flex-col gap-3">
                {
                    activityData.price && activityData.price.length > 0 && activityData.price.map((bundle) => {
                        const { label, detail } = getBundleLabel(bundle, t);
                        const type = bundle.type || 'flat';
                        const selected = isSelected(bundle._id);
                        const quantity = getQuantity(bundle._id);
                        const unitLabel = type === 'perHour'
                            ? t('BookingWizard.option.hours')
                            : t('BookingWizard.option.participants');
                        // Opzione sold out (flag manuale dell'admin): grigia e non cliccabile.
                        // La dicitura "SOLD OUT" arriva dal server in coda alla nota.
                        const isSoldOut = Boolean(bundle.isSoldOut);
                        const buttonStyle = isSoldOut
                            ? 'border-neutral-200 bg-neutral-100 cursor-not-allowed'
                            : `${selected ? 'border-[#225424] bg-green-50' : 'border-neutral-200 bg-white'} hover:bg-neutral-100`;
                        return (
                            <div key={bundle._id}>
                                <button
                                    className={`w-full border-2 ${buttonStyle} p-4 rounded-xl text-center`}
                                    onClick={() => selectBundle(bundle)}
                                    disabled={isSoldOut}
                                >
                                    <div className={`font-bold ${isSoldOut ? 'text-neutral-400' : ''}`}>{label}</div>
                                    {detail && <div className="text-sm text-neutral-500">{detail}</div>}
                                    {bundle.note && <div className="text-sm text-neutral-500">{bundle.note}</div>}
                                </button>
                                {
                                    // Stepper quantità sotto ogni opzione selezionata (solo perPerson/perHour)
                                    selected && type !== 'flat' &&
                                    <div className="border-2 border-t-0 border-[#225424] rounded-b-xl mx-3 p-4 flex flex-col items-center gap-3 bg-white">
                                        <div className="text-sm text-neutral-500">{unitLabel}</div>
                                        <div className="flex items-center gap-6">
                                            <button className="w-12 h-12 rounded-full border-2 border-[#225424] text-[#225424] text-2xl" onClick={() => stepQuantity(bundle, -1)}>−</button>
                                            <div className="text-2xl font-bold w-10 text-center">{quantity}</div>
                                            <button className="w-12 h-12 rounded-full border-2 border-[#225424] text-[#225424] text-2xl" onClick={() => stepQuantity(bundle, 1)}>+</button>
                                        </div>
                                        {
                                            quantity > 0 &&
                                            <div className="text-center">
                                                <div className="text-sm text-neutral-500">{t('BookingWizard.option.totalCalc', { value: bundle.value, qty: quantity, unit: unitLabel.toLowerCase() })}</div>
                                                {
                                                    // Con una sola selezione il totale della riga È il totale complessivo
                                                    selections.length === 1 && total != null &&
                                                    <div className="font-bold">{t('BookingWizard.option.total', { total })}</div>
                                                }
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        );
                    })
                }
            </div>

            {/* Totale complessivo quando si combinano più opzioni */}
            {
                selections.length > 1 && total != null &&
                <div className="w-full flex justify-between items-center px-2 font-bold">
                    <div>{t('BookingWizard.option.total', { total })}</div>
                    <div>{getPerPersonSummary(activityData, booking, t)}</div>
                </div>
            }

            {/* Descrizione della prenotazione (priceNote) — HTML Quill dell'admin, nessun titolo
                preposto: compare così com'è formattata (il server ha già normalizzato i link interni) */}
            {
                htmlHasText(activityData.priceNote) &&
                <div
                    className="w-full border-t pt-4"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activityData.priceNote) }}
                    onClick={handleContentLinkClick}
                ></div>
            }

            {
                canProceed &&
                <button className="bg-[#225424] py-3 px-6 text-white rounded-xl w-full md:w-fit" onClick={() => navigate('../data')}>
                    {t('BookingWizard.proceed')}
                </button>
            }
        </div>
    );
};

// "N partecipanti" accanto al totale combinato (Σ quantità delle opzioni perPerson selezionate)
const getPerPersonSummary = (activityData, booking, t) => {
    const count = (booking.selections || []).reduce((sum, selection) => sum + (Number(selection.quantity) || 0), 0);
    return `${count} ${t('BookingWizard.option.participants').toLowerCase()}`;
};

export default StepOption;
